import {IActionable, IContextMenuActions, IContextMenuActionsPointer, IFocusActionables, OActionableListInit} from "back/commons/actionables";
import {BaseElement, OSkinableInit} from "back/commons/basis";
import {ActionBtn, OActionBtnInit} from "back/commons/widgets/buttons";
import {PopupTooltip} from "back/commons/widgets/popups";
import {FastFindItem} from "back/wsp/dialogs/fastFindItem";
import {ItemCreator} from "back/wsp/dialogs/itemCreator";
import {IItemUiEnv} from "back/wsp/views/itemMain";
import {AccelKeyMgr, Action, ACTION, EButtonUiContext, IAccelKeyMgrPointer, IAction} from "lib/commons/actions";
import {EventMgr} from "lib/commons/events";
import {IInfoBroker, IInfoBrokerPointer} from "lib/commons/infos";
import {IReg, REG} from 'lib/commons/registry';
import {renderAppend, TemplateResult, xhtml} from "lib/commons/utils/htmlLit";
import {JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {InfoHighlighItemSgn, IShortDescCtx, ITEM, OTransferSrcContext, srcRefSub} from "lib/wsp/item";
import {JSrcFields, SRC, srcRef} from "lib/wsp/src";
import {IWspEnv, IWspUiEnv, JWspUriChangeMsg} from "lib/wsp/wsp";
import {IDirItemType, ItemType, UTILS} from "lib/wsp/wspMetaUi";
import {FocusItem, SrcAction} from "back/wsp/actions/srcActions";
import {ISearchCrit, SearchItemSgnRegexp} from "lib/wsp/search";
import {IWedletActionCtx} from "back/edit/wed/wedlets/wedlet";
import {isEltBoxSelection} from "back/edit/wed/features/boxSel";
import {EWspChangesEvts, WspsLivePlace} from "lib/wsp/wspsLive";
import {IFormJsonisable} from "lib/commons/forms";
import {OUserRefInit} from "back/core/widgets/userRef";
import {LOCALE} from "lib/commons/lang";

/**
 * Dessine une ref à un item (voir un espace) selon un template HTML et optionnellement un template d'un tooltip.
 *
 * Si aucune référence n'est renseignée (srcRef=""), une classe "empty" est ajoutée au widget: utile pour la notion
 * de champ obligatoire.
 *
 * Attributs:
 * Config init : tpl, tpl-over, show-status
 * States : srcRef
 */
export interface SrcDrawer {
	initialize(init: OSrcDrawerInit): this
}

export interface OSrcDrawerInit extends OSkinableInit {
	reg?: IReg<IWspEnv>

	showStatus?: boolean

	tplSvc?: string
	tpl?: (itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer) => TemplateResult

	tplOverSvc?: string
	tplOver?: (itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer) => TemplateResult

	/**
	 * Si renseigné, le widget s'abonnera à cette place pour le refresh auto, sinon tentera this.reg.env.place.
	 * Si "none", le widget ne s'abonnera pas (affichages éphémères ou en masse, gestion du refresh géré par l'appelant).
	 */
	place?: WspsLivePlace | "none"

	/** Si le srcRef est connu à l'init, mais pas les shortDesc. */
	srcRef?: srcRefSub

	/** Si on dispose du shortDesc à afficher, srcRef est inutile. */
	shortDesc?: JSrcFields
}

export class SrcDrawer extends BaseElement implements IShortDescCtx {

	reg: IReg<IWspUiEnv & IInfoBrokerPointer>;

	config: OSrcDrawerInit;

	/** API IShortDescCtx */
	shortDescs: JSrcFields[];

	/** Non null si chargement en cours. */
	fetchPending: Promise<JSrcFields>;

	protected _srcRefSub: srcRefSub | null;

	protected _fields: JSrcFields;

	/** Mémoire du listener des WspChanges pour delete. */
	protected _lstn: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	/**
	 * Si space, retourne l'itemType NotFound.
	 * Si wspMetaUi indispo, retourne null.
	 */
	protected _getItemType(): ItemType | null {
		try {
			return this._fields ? this.reg.env.wsp.wspMetaUi.getItemType(this._fields.itModel) : this.reg.env.wsp.wspMetaUi.itemTypeNull;
		} catch (e) {
			//En cas de déchargement ou invalidité du wsp, la chaine this.reg.env.wsp.wspMetaUi... peut produire un NPE.
			return null;
		}
	}

	/** Listeners sur chgt de la source. */
	protected _onSrcRefChange: EventMgr<(drawer: SrcDrawer) => void>;

	protected _initialize(init: OSrcDrawerInit) {
		this.config = init || {};
		if (!this.reg) this.reg = this.findReg(init);
		if (!this.config.place) this.config.place = this.reg.env.place; //set default place si this.config.place != "none"
		if (this.config.tplSvc) this.config.tpl = this.reg.getSvc(this.config.tplSvc);
		if (this.config.tplOverSvc) this.config.tplOver = this.reg.getSvc(this.config.tplOverSvc);
		if (!this.config.tpl) this.config.tpl = SRCDRAWER.itemCodeTpl;
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.nodeName, init);
		if (init.tplOver) {
			this.addEventListener('pointerenter', function (this: SrcDrawer, ev) {
				const tt = new PopupTooltip().initialize({
					tooltipOf: this,
					skinOver: 'wsp-src-drawer/tooltip',
					hoverAllowed: true
				});
				tt.addEventListener('c-show', function (this: PopupTooltip, ev) {
					const drawer = this.tooltipOf as SrcDrawer;
					const itemType = drawer._getItemType();
					const tpl = itemType ? drawer.config.tplOver(itemType, drawer._fields, drawer) : null;
					if (tpl) renderAppend(tpl, this.contentRoot);
					else this.close();
				});
				tt.forwardPointerEnter(ev);
			}, {once: true});
		}
		if (!this._srcRefSub) this._srcRefSub = init.srcRef || null;
		if (init.shortDesc) {
			this.redraw(init.shortDesc);
		} else this.fetchSrc();
	}

	buildInitFromAtts(init?: OSrcDrawerInit): OSrcDrawerInit {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute("srcRef")) init.srcRef = this.getAttribute("srcRef");
		if (this.hasAttribute("tpl")) init.tplSvc = this.getAttribute("tpl");
		if (this.hasAttribute("tpl-over")) init.tplSvc = this.getAttribute("tpl-over");
		if (this.hasAttribute("show-status")) init.showStatus = this.getAttribute("show-status") === "true";
		return init;
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.config.place instanceof WspsLivePlace && this._refreshFreeze === 0) {
			this._lstn = this.onWspUriChange.bind(this);
			this.config.place.eventsMgr.on('wspUriChange', this._lstn);
		}
	}

	disconnectedCallback() {
		if (this.config.place instanceof WspsLivePlace && this._refreshFreeze === 0) this.config.place.eventsMgr.removeListener('wspUriChange', this._lstn);
	}

	/** Si le srcRef est connu à l'init, mais pas les shortDesc. */
	get srcRef(): srcRef {return ITEM.extractSrcRef(this._srcRefSub)}

	get srcRefSub(): srcRefSub {return this._srcRefSub}

	/**
	 *
	 * @param srcRefSub
	 * @param fields
	 *  Si renseigné, pris tel quel.
	 *  Si null, force un chargement du shortDesc
	 *  Si undefined, charge le shortDesc si srcRef a changé
	 */
	setSrcRef(srcRefSub: srcRefSub, fields?: JSrcFields): void | Promise<void> {
		if (fields) srcRefSub = ITEM.srcRefSub(fields); //on normalise srcRef
		if (this._srcRefSub === srcRefSub && fields === undefined) return; //même src, pas de chgt explicite des fields.
		this._srcRefSub = srcRefSub;
		if (this.initialized) {
			if (fields) this.redraw(fields);
			else return this.fetchSrc();
		}
	}

	/** ShortDesc actuellement chargé. Voir .fetchPending pour détecter un autre chargement est en cours. */
	get shortDesc() {return this._fields}

	get onSrcRefChange(): EventMgr<(drawer: SrcDrawer) => void> {return this._onSrcRefChange || (this._onSrcRefChange = new EventMgr())}

	async fetchSrc() {
		const srcRef = this._srcRefSub;
		if (srcRef) {
			this.fetchPending = this.reg.env.wsp.fetchShortDesc(srcRef, this);
			try {
				const fields = await this.fetchPending;
				if (srcRef === this._srcRefSub) this.redraw(fields); //check raceCond
			} catch (e) {
				this.redraw(null); //FIXME créer shortDesc en erreur
			} finally {
				this.fetchPending = null;
			}
		} else this.redraw(null);
	}

	protected redraw(fields: JSrcFields | null) {
		this._srcRefSub = fields ? ITEM.srcRefSub(fields) : null;// (ré)affecte, normalise le srcRef
		this._fields = fields; //Mémoire utile notamment pour le tooltip : redraw async.
		if (this.shortDescs) {
			//API IShortDescCtx
			if (fields) this.shortDescs[0] = fields;
			else this.shortDescs.length = 0;
		}
		this.classList.toggle("empty", fields == null);
		const lastCh = this.shadowRoot.lastElementChild;
		const itemType = this._getItemType(); //Si null wspMetaUi HS
		renderAppend(itemType ? this.config.tpl(itemType, fields, this) : xhtml`<span/>`, this.shadowRoot);
		if (lastCh.localName !== "slot") this.shadowRoot.appendChild(<slot/>);
		if (this._onSrcRefChange) this._onSrcRefChange.emit(this);
	}

	protected onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'house' | 'server') {
		if (msg.wspCd !== this.reg.env.wsp.code) return;
		if (msg.type === EWspChangesEvts.r) {
			if (this._fields && SRC.isSubUriOrEqual(msg.srcUri, this._fields.srcUri)) this.fetchSrc();
		} else if (this.srcRef === SRC.srcRef(msg)) {
			if (msg.type === EWspChangesEvts.u) this.fetchSrc();
			else if (this.config.showStatus && msg.type === EWspChangesEvts.s) {
				if (this._fields) {
					this._fields['itSt'] = msg.itSt;
					this.redraw(this._fields);
				} else this.fetchSrc();
			}
		}
	}

	//*** API IShortDescCtx ***

	get infoBroker(): IInfoBroker {return this.reg.env.infoBroker}

	get emitter(): HTMLElement {return this}
}

REG.reg.registerSkin('wsp-src-drawer', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		padding: 2px;
		margin: 0 .5em;
	}

	.code {
		color: var(--fade-color);
		font-size: var(--label-size);
	}

	.icon {
		filter: var(--filter);
		vertical-align: text-bottom;
		user-select: none;
	}

	.itTi {
		color: var(--alt1-color);
		font-size: var(--label-size);
	}

	.imgView, .videoView, .audioView, .iframeView, .dynGenView {
		max-height: 10em;
		object-fit: scale-down;
		max-width: 100%;
	}

	.audioView {
		max-height: 2em;
	}

	.iframeView, .dynGenView {
		border: 1px solid var(--border-color);
		width: 100%;
	}
`);

customElements.define('wsp-src-drawer', SrcDrawer);


export class SrcDrawerInline extends SrcDrawer {
}

REG.reg.registerSkin('wsp-src-drawer-inline', 1, /* language=CSS */ `
	:host {
		white-space: nowrap;
		user-select: none;
	}

	.imgView, .videoView, .audioView, .iframeView, .dynGenView {
		max-height: 5em;
		vertical-align: bottom;
	}

	.audioView {
		max-height: 2em;
	}

	:host(.fixedIconSize) .icon {
		max-height: var(--icon-size);
		width: var(--icon-size);
	}

	.code,
	.itTi {
		color: var(--fade-color);
		font-size: var(--label-size);
	}

	.icon {
		filter: var(--filter);
		vertical-align: text-bottom;
		max-height: 1 lh;
		max-height: 1.4em;
	}
`);

customElements.define('wsp-src-drawer-inline', SrcDrawerInline);

/** Skin pour SrcDrawer affiché en tooltip. */
REG.reg.registerSkin('wsp-src-drawer/tooltip', 1, /* language=CSS */ `
	#content {
		flex: 1 1 auto;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.details {
		font-size: var(--label-size);
		padding: 0;
		margin: .5em;
	}

	.details > li {
		display: block;
		max-width: 40vw;
	}

	.itTi {
		font-weight: bold;
	}

	.val {
		user-select: all;
	}

	.itemPreview {
		border: 1px solid var(--border-color);
		max-height: 40vh;
		max-width: 40vw;
	}

	.imgView, .videoView, .audioView, .iframeView, .dynGenView {
		max-width: 40vw;
		max-height: 40vh;
	}

	.audioView {
		max-height: 2em;
	}
`);

/**
 * Objet focusable
 * Implémentation copier/coller, drag-drop, lien à un itemSelector,
 * Interaction avec une focusToolbar lorsqu'on a le focus.
 */

export interface SrcPointer extends SrcDrawer, IFocusActionables<IShortDescCtx> {
	initialize(init?: OItemPointerInit): this
}

export interface OItemPointerInit extends OSrcDrawerInit {

	/** Signatures d'items autorisées (exploité par les actions d'édition). */
	sgnPattern?: RegExp

	/** Signatures des subItems autorisées (exploité par les actions d'édition). */
	subSgnPattern?: RegExp

	/** Active le dispatch d'un IInfoHighlighItemSgn auprès de l'infoBroker lorsque ce ptrItem détient le focus. */
	dispatchHighlight?: boolean

	readOnly?: boolean;

	/**
	 * Surcharge des listes d'actions (listes de Action<IShortDescCtx>).
	 * Valeur par défaut : ["actions:wsp:src-pointer", "actions:wsp:shortDesc"]
	 *
	 * Note : pour obtenir un widget sans action visible en écriture, supprimer ou remplacer "actions:wsp:src-pointer".
	 */
	actionsLists?: string[]

	/** Action au double-click. */
	defaultAction?: IAction<IShortDescCtx>

	/**
	 * Surcharge des racourcis claviers (listes de Action<IShortDescCtx>).
	 * Valeur par défaut : "accelkeys:wsp:src-pointer", "accelkeys:wsp:shortDesc"
	 */
	accelKeysLists?: string[]

	draggable?: boolean
	dropable?: boolean

	/** Sur drop en particulier, permet-on la création de contenu à partir d'une source externe (File, blob, url...) ? */
	srcCreatable?: boolean
}

export class SrcPointer extends SrcDrawer implements IFocusActionables<IShortDescCtx>, IContextMenuActionsPointer<IShortDescCtx>, IAccelKeyMgrPointer<IShortDescCtx>, IFormJsonisable {

	actionsLists: string[];

	accelKeyMgr: AccelKeyMgr<IShortDescCtx>;

	readOnly: boolean;

	config: OItemPointerInit;

	/** nom de la propriété JSON à renseigner si utilisation de IFormJsonisable **/
	propName?: string;

	get actionContext(): IShortDescCtx {return this}

	protected _sgnPattern: RegExp;

	protected _subSgnPattern: RegExp;

	constructor() {
		super();
		this.shortDescs = [];
	}

	openFastSelect(): Promise<void> {
		return SrcPointerFastSelect.SINGLETON.execute(this);
	}

	get focusActionables(): IActionable<IShortDescCtx>[] {
		return ActionBtn.mergeActionablesForBar<IShortDescCtx>(this.reg, this.reg.getPref("groupOrder.wsp.shortDesc", ""), this, ...this.actionsLists);
	}

	get ctxMenuActions(): IContextMenuActions<IShortDescCtx> {
		let actions = this.reg.mergeLists<IAction<IShortDescCtx>>(...this.actionsLists);
		actions = ACTION.injectSepByGroup(actions, this.reg.getPref("groupOrder.wsp.shortDesc", ""), this);
		return actions && actions.length > 0 ? {actions, actionContext: this.actionContext} : null;
	}

	get sgnPattern(): RegExp {return this._sgnPattern}

	get subSgnPattern(): RegExp {return this._subSgnPattern}

	protected _initialize(init: OItemPointerInit) {
		this.tabIndex = 0;
		if (!init.skin) init.skin = 'wsp-src-drawer';
		if (!init.skinOver) init.skinOver = 'wsp-src-pointer';
		super._initialize(init);

		if (init.defaultAction) this.addEventListener('dblclick', this.onDblClick);

		if (init.readOnly) this.readOnly = init.readOnly;
		if (init.sgnPattern) this._sgnPattern = init.sgnPattern;
		if (init.subSgnPattern) this._subSgnPattern = init.subSgnPattern;
		this.actionsLists = init.actionsLists || ACTIONS;
		this.accelKeyMgr = ACTION.mergeAccelKeyMgr<IShortDescCtx>(this.reg, ...(init.accelKeysLists || ACCELKEYS));
		if (this.accelKeyMgr) this.addEventListener('keydown', function (this: SrcPointer, ev: KeyboardEvent) {
			this.accelKeyMgr.handleKeyboardEvent(ev, this.actionContext);
		});
		if (init.draggable) {
			this.setAttribute("draggable", "true");
			this.addEventListener('dragstart', function (this: SrcPointer, ev: DragEvent) {
				if (this.shortDescs[0]) ITEM.setShortDescTransferToDragSession(this, ev, 'native');
			});
			this.addEventListener('dragend', ITEM.resetShortDescTransferToDragSession);
		}
		if (init.dropable) {
			const transferCtx: OTransferSrcContext = {targetSrcType: "item", canCreate: init.srcCreatable};
			this.addEventListener('dragover', function (this: SrcPointer, ev: DragEvent) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				const shortDescs = ITEM.getShortDescsTransferFromDragSession(this.reg, ev.dataTransfer, transferCtx, this);
				ev.dataTransfer.dropEffect = !shortDescs || shortDescs.srcRefs.length < 1 || shortDescs.wspCd !== this.reg.env.wsp.code ? "none" : "copy"; //on autorise le drop
			});
			this.addEventListener('drop', function (this: SrcPointer, ev: DragEvent) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				const shortDescs = ITEM.getShortDescsTransferFromDragSession(this.reg, ev.dataTransfer, transferCtx, this);
				if (!shortDescs || shortDescs.isImport || shortDescs.srcRefs.length < 1 || shortDescs.wspCd !== this.reg.env.wsp.code) return;
				this.setSrcRef(shortDescs.srcRefs[0], shortDescs.shortDescs[0]);
			});
		}

		this._initDispatchHighlight(init);
	}

	protected _initDispatchHighlight(init: OItemPointerInit) {
		if (init.dispatchHighlight && this.reg.env.infoBroker) {
			this.addEventListener('focus', this.onFocus);
			this.addEventListener('blur', this.onBlur);
		}
	}

	protected redraw(fields: JSrcFields | null) {
		super.redraw(fields);
		if (this.config.dispatchHighlight && this.matches(":focus")) {
			const infoBroker = this.reg.env.infoBroker;
			if (infoBroker) infoBroker.dispatchInfo(new InfoHighlighItemSgn(this.sgnPattern, this.srcRef), this);
		}
	}

	onFocus(ev: FocusEvent) {
		const infoBroker = this.reg.env.infoBroker;
		if (infoBroker) infoBroker.dispatchInfo(new InfoHighlighItemSgn(this.sgnPattern, this.srcRef), this);
	}

	onBlur(ev: FocusEvent) {
		const infoBroker = this.reg.env.infoBroker;
		if (infoBroker) infoBroker.dispatchInfo(new InfoHighlighItemSgn(null), this);
	}

	onDblClick(ev: MouseEvent) {
		const act = this.config.defaultAction;
		if (act) act.executeIfAvailable(this, ev);
	}

	fillJson(parent: any, root: any): void {
		if (this.propName)
			parent[this.propName] = this.srcRef;
	}

	extractJson(parent: Dict<any>): boolean {
		if (this.propName && this.propName in parent) {
			this.setSrcRef(parent[this.propName]);
			return true;
		}
		return false;
	}
}

customElements.define('wsp-src-pointer', SrcPointer);

/** Surcharges du skin wsp-src-drawer */
REG.reg.registerSkin('wsp-src-pointer', 1, /* language=CSS */ `
	:host {
		cursor: pointer;
	}

	:host(:focus) {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}
`);


export class SrcPointerInline extends SrcPointer {

	protected _initialize(init: OItemPointerInit) {
		if (!init.skin) init.skin = 'wsp-src-drawer-inline';
		if (!init.skinOver) init.skinOver = 'wsp-src-pointer-inline';
		super._initialize(init);
	}

}

customElements.define('wsp-src-pointer-inline', SrcPointerInline);

/** Surcharges du skin wsp-src-drawer-inline */
REG.reg.registerSkin('wsp-src-pointer-inline', 1, /* language=CSS */ `
	:host {
		cursor: pointer;
	}

	:host(:focus) {
		outline: var(--focus-outline);
		outline-offset: -2px;
	}
`);

export type OSrcBtnInit<C> = OActionBtnInit<C> & OSrcDrawerInit;

/**
 * Principalement pour affichage de liste de src en menu...
 */
export interface SrcBtn<C> extends ActionBtn<C> {
	initialize(init?: OSrcBtnInit<C>): this
}

export class SrcBtn<C> extends ActionBtn<C> {

	protected _initialize(init: OSrcBtnInit<C>) {
		init.skin = "wsp-src-btn";
		super._initialize(init);
		init.skin = 'wsp-src-drawer-inline';
		if (!init.place) init.place = "none";
		this.shadowRoot.appendChild(new SrcDrawer().initialize(init));
	}

	protected _refresh() {
	}
}

//TODO gestion checked
REG.reg.registerSkin('wsp-src-btn', 1, /* language=CSS */ `
	:host {
		display: flex;
	}

	:host(:focus) {
		background-color: var(--row-inSel-bgcolor);
		outline: none;
	}
`);

customElements.define('wsp-src-btn', SrcBtn);


/**
 * Action construisant un SrcBtn pour affecter une srcRef à un SrcPointer.
 * Utilisé dans les l'affichage de listes d'items : historiques...
 */
export class SrcPointerSetterAction extends Action<SrcPointer> {

	constructor(public shortDesc: JSrcFields) {
		super();
	}

	buildCustomButton(ctx: SrcPointer, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		return new SrcBtn<SrcPointer>().initialize({
			reg: ctx.reg,
			action: this,
			actionContext: ctx,
			shortDesc: this.shortDesc,
			uiContext: uiContext,
			role: uiContext === 'menu' ? 'menuitem' : 'button'
		});
	}

	execute(ctx: SrcPointer, ev?: Event): any | "noPreventDefault" {
		ctx.setSrcRef(SRC.srcRef(this.shortDesc), this.shortDesc);
	}
}


const ACTIONS = ["actions:wsp:src-pointer", "actions:wsp:shortDesc"];
const ACCELKEYS = ["accelkeys:wsp:src-pointer", "accelkeys:wsp:shortDesc"];


export class SrcPointerWriteAction extends Action<SrcPointer> {
	isEnabled(ctx: SrcPointer) {
		if (ctx.readOnly) return false;
		return super.isEnabled(ctx);
	}
}


export class SrcPointerPaste extends SrcPointerWriteAction {
	static SINGLETON = new SrcPointerPaste();

	constructor() {
		super('paste');
		this.setLabel("Coller");
		this.setGroup("clipboard");
	}

	async execute(ctx: SrcPointer, ev?: Event) {
		const wsp = ctx.reg.env.wsp;
		const shortDescs = await ITEM.getShortDescsTransferFromClipboard(wsp, ctx.emitter);
		if (!shortDescs || Array.isArray(shortDescs)) return;
		if (shortDescs.isImport || shortDescs.srcRefs.length < 1 || shortDescs.wspCd !== wsp.code) return;
		ctx.setSrcRef(shortDescs.srcRefs[0], shortDescs.shortDescs[0]);
	}
}

export class SrcPointerCreateItem extends SrcPointerWriteAction {
	constructor() {
		super('create');
		this.setLabel("Créer un item...");
		this._icon = "/@skin@/wsp/actions/createItem.svg";
		this.setGroup("edit");
		// NOTE : pas d'évaluation de la perm 'create.item' car la création peut avoir lieu ailleurs que dans le contexte courant
	}

	isVisible(ctx: SrcPointer): boolean {
		if (!ctx.reg.getPref("wsp.createItem", true)) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: SrcPointer, ev?: Event) {
		const regexp = ctx.sgnPattern;
		const itemCtx = (ctx.reg.env as IItemUiEnv).longDesc;
		const newItem = await ItemCreator.openCreateItem(ItemCreator.setDefaultConfigFromReg(ctx.reg, {
			itemTypesTree: {
				itemTypeReducer: regexp ? (acc: ItemType[], cur: ItemType) => {
					if (regexp.test(cur.getSgn())) acc.push(cur);
					return acc;
				} : null,
				textFilter: ""
			},
			spaceUri: itemCtx ? ITEM.extractSpaceUri(itemCtx.srcUri) : ""
		}), "Créer un item...", ctx,).onNextClose();
		if (newItem) ctx.setSrcRef(SRC.srcRef(newItem), newItem);
	}
}

/**
 * Action d'affichage du FastFind à partir d'un SrcPointer.
 */
export class SrcPointerFastSelect extends SrcPointerWriteAction {

	static SINGLETON = new SrcPointerFastSelect();

	customCrit?: ISearchCrit;

	constructor(id?:string) {
		super(id || "fastSelect");
		this._label = "Sélection rapide...";
		this._icon = "/@skin@/wsp/actions/fastFind.svg";
		this._group = 'ptrEdit';
	}

	setCustomCrit(crit: ISearchCrit): this {
		this.customCrit = crit;
		return this;
	}

	async execute(ctx: SrcPointer, ev?: Event) {
		//Chargement asynch du fastFind
		const {FastFindItem} = await import("back/wsp/dialogs/fastFindItem.js");
		const currentSrc = (ctx.reg.env as IItemUiEnv).longDesc;
		const sgnPattern = ctx.sgnPattern;
		const sd = await FastFindItem.openFastFind({
			reg: ctx.reg,
			restrictions: this.getRestrictions(ctx, ev),
			srcFilter: this.getSrcFilter(ctx, ev),
			itemCreator: ctx.reg.getPref("wsp.createItem", true) ? ItemCreator.setDefaultConfigFromReg(ctx.reg, {
				spaceUri: currentSrc ? ITEM.extractSpaceUri(currentSrc.srcUri) : "",
				itemTypesTree: {
					itemTypeReducer: sgnPattern ? (acc: ItemType[], cur: ItemType) => {
						if (sgnPattern.test(cur.getSgn())) acc.push(cur);
						return acc;
					} : null
				}
			}) : undefined,
			subItemsFind: ctx.subSgnPattern ? {subItemSgnPattern: ctx.subSgnPattern} : undefined
		}, ctx, ev).onNextClose();
		//console.log('SrcPointerFastSelect', sd);
		ctx.focus();
		if (sd) ctx.setSrcRef(ITEM.srcRefSub(sd), sd);
	}

	protected getRestrictions(ctx: SrcPointer, ev?: Event): ISearchCrit {
		if (this.customCrit) return this.customCrit;
		const sgnPattern = ctx.sgnPattern;
		return sgnPattern ? new SearchItemSgnRegexp(sgnPattern.source) : null
	}

	protected getSrcFilter(ctx: SrcPointer, ev?: Event): (data: JSrcFields) => boolean {
		return null;
	}
}

export class SrcPointerErase extends SrcPointerWriteAction {
	constructor() {
		super('erase');
		this.setLabel("Effacer");
		this.setGroup("ptrEdit");
	}

	isVisible(ctx: SrcPointer): boolean {
		if (ctx.srcRef == null) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: SrcPointer, ev?: Event) {
		ctx.setSrcRef(null);
	}
}

/** Action dédié au double-click : focus l'item si défini et infoBroker présent, sinon ouvre le fastSelect pour remplacer si contexte editable. */
export class SrcPointerDblClick extends SrcAction<SrcPointer> {

	static SINGLETON = new SrcPointerDblClick();

	constructor() {
		super('focusItemOrSelect');
	}

	isEnabled() {return true}

	execute(ctx: SrcPointer, ev?: Event) {
		const sd = ctx.shortDescs[0];
		if (ctx.infoBroker && sd?.srcUri != null) {
			FocusItem.gotoItem(ctx.infoBroker, sd, ctx.emitter);
			ev?.preventDefault();
			ev?.stopImmediatePropagation();
		}	else if (!(ctx.emitter as SrcPointer).readOnly) {
			SrcPointerFastSelect.SINGLETON.executeIfAvailable(ctx, ev);
			ev?.preventDefault();
			ev?.stopImmediatePropagation();
		}
	}
}


function addAction(action: Action<IShortDescCtx>, accel?: string, order?: number) {
	REG.reg.addToList("actions:wsp:src-pointer", action.getId(), 1, action, order);
	if (accel) REG.reg.addToList("accelkeys:wsp:src-pointer", accel, 1, action);
}

addAction(SrcPointerFastSelect.SINGLETON, "F4");
addAction(new SrcPointerCreateItem());
addAction(SrcPointerPaste.SINGLETON, "v-accel", 10);
addAction(new SrcPointerErase, "Delete", 15);

export namespace SRCDRAWER {

	export function itemCodeTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`<span class="itemCode"><img class="icon" src="${itemType.getIcon(srcDrawer.config.showStatus ? fields : null)}" draggable="false"/><span class="code">${fields ? ITEM.getSrcMainName(srcDrawer.reg, fields, itemType.wspMeta) : ""}</span></span>`;
	}

	/** Code seul, suppr de la class .code pour stylage normal, sans privilégier le title, etc */
	export function itemCodeAloneTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`<span class="itemCode"><img class="icon" src="${itemType.getIcon(srcDrawer.config.showStatus ? fields : null)}" draggable="false"/><span>${fields ? ITEM.getSrcMainName(srcDrawer.reg, fields, itemType.wspMeta) : ""}</span></span>`;
	}

	export function itemTitleTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`<span class="itTi">${fields ? ITEM.getTitle(fields) : ""}</span>`;
	}

	export function itemIconTitleTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`<span class="itTi"><img class="icon" src="${itemType.getIcon(srcDrawer.config.showStatus ? fields : null)}" draggable="false"/>${fields ? ITEM.getTitle(fields) : ""}</span>`;
	}

	export function itemTitleOrCodeTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return (fields && ITEM.getTitle(fields)) ? itemIconTitleTpl(itemType, fields, srcDrawer) : itemCodeTpl(itemType, fields, srcDrawer);
	}

	export function itemCodeAndTitleTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`${itemCodeTpl(itemType, fields, srcDrawer)}<span class="itTi">${fields ? ITEM.getSrcSecondName(srcDrawer.reg, fields, itemType.wspMeta) : null}</span>`;
	}

	export function itemUriTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`<span part="itemUri"><img class="icon" part="icon" src="${itemType.getIcon(srcDrawer.config.showStatus ? fields : null)}" draggable="false"/><span part="uri">${fields ? ITEM.extractItemUri(fields.srcUri) : ""}</span></span>`;
	}

	export function previewTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		if (!fields || !itemType) return undefined;
		const wsp = srcDrawer.reg.env.wsp;
		if (fields.itSubItem) {
			const subTitemType = wsp.wspMetaUi.getItemType(fields.itSubItem.mo);
			return subTitemType?.getPreview(wsp, fields, srcDrawer.reg, UTILS);
		}
		return itemType.getPreview(wsp, fields, srcDrawer.reg, UTILS);
	}

	export function previewOrItemCodeTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return previewTpl(itemType, fields, srcDrawer) || itemCodeTpl(itemType, fields, srcDrawer);
	}

	export function previewOrItemTitleOrCodeTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return previewTpl(itemType, fields, srcDrawer) || itemTitleOrCodeTpl(itemType, fields, srcDrawer);
	}

	export function previewAndItemCodeTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`${previewTpl(itemType, fields, srcDrawer)}${itemCodeTpl(itemType, fields, srcDrawer)}`;
	}

	export function previewAndItemCodeAndTitleTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`${previewTpl(itemType, fields, srcDrawer)}${itemCodeTpl(itemType, fields, srcDrawer)}<span class="itTi">${fields ? ITEM.getSrcSecondName(srcDrawer.reg, fields, itemType.wspMeta) : null}</span>`;
	}

	export function detailsTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		if (!fields) return xhtml``;
		//let odb = srcDrawer.reg.env.wsp.backEnd === 'odb';
		//Space
		if (fields.itModel == null) return xhtml`<ul class="details">
<li><span class="val srcUri" title="Chemin complet">${ITEM.getItemUriLabel(fields.srcUri, fields)}</span></li>
<li><span class="val itModel">Espace</span></li>
${srcDate(itemType, fields, srcDrawer)}
</ul>`;
		//Item
		return xhtml`<ul class="details">
<li><span class="val itTi" title="Titre">${ITEM.getTitle(fields)}</span></li>
<li><span class="val itModel" title="Modèle d'item">${itemType ? itemType.getTitle(fields) : undefined}</span></li>
${srcDate(itemType, fields, srcDrawer)}
<li><span class="val srcUri" title="Chemin complet">${ITEM.getItemUriLabel(fields.srcUri, fields)}</span></li>
</ul>`;
	}

	export function detailsItemIconPreviewTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		if (!fields) return xhtml``;
		//Item
		return xhtml`<ul class="details">
<li><span class="val itTi" title="Titre">${ITEM.getTitle(fields)}</span></li>
<li><span class="val itModel" title="Modèle d'item"><img class="icon" src="${itemType.getIcon(srcDrawer.config.showStatus ? fields : null)}" draggable="false"/>${itemType ? itemType.getTitle(fields) : undefined}</span></li>
${srcDate(itemType, fields, srcDrawer)}
<li><span class="val srcUri" title="Chemin complet">${ITEM.getItemUriLabel(fields.srcUri, fields)}</span></li>
</ul>${previewTpl(itemType, fields, srcDrawer)}`;
	}

	export function detailsPreviewTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`${detailsTpl(itemType, fields, srcDrawer)}${previewTpl(itemType, fields, srcDrawer)}`;
	}

	export function srcDate(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		//Formatage de la date + du user.
		return !fields || !fields.srcDt ? null : xhtml`<li><span class="val srcDt" title="Dernière modification">
			${fields ? LOCALE.formatDateDigitsToSec.format(new Date(fields.srcDt)) : undefined}
			${fields && fields.srcUser ? xhtml`<span> par <c-userref class="inline" i.="${{reg: srcDrawer.reg, nickOrAccount: fields.srcUser} as OUserRefInit}"/></span>` : undefined}
			</span></li>`;
	}

	export function typesAllowedTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcPointer): TemplateResult {
		const regexp = srcDrawer.sgnPattern;
		if (!regexp) return null;
		const datas = itemType.wspMeta.getItemTypesTree((acc, curr) => {
			if (regexp.test(curr.getSgn())) acc.push(curr);
			return acc;
		});

		function draw(data: IDirItemType | ItemType): TemplateResult {
			if (data instanceof ItemType) {
				return xhtml`<li><img src="${data.getIcon()}"/>${data.getTitle()}</li>`
			} else {
				return xhtml`<li><h6>${data.label}</h6><ul>${data.children.map(draw)}</ul></li>`
			}
		}
		const title = "Types d'items autorisés";
		return xhtml`${srcDrawer.reg.createSkin("src-drawer/itemTypes")}${SRCDRAWER.wedBarActions(itemType, fields, srcDrawer)}<div><h5>${title}</h5><ul>${datas.map(draw)}</ul></div>`;
	}

	REG.reg.registerSkin("src-drawer/itemTypes", 1, /* language=CSS */ `
	  div {
		  padding: 2px;
	  }

	  h5, h6 {
		  font-size: 1em;
		  margin: 0;
		  padding: .2em;
	  }

	  h5 {
		  border-top: 1px solid var(--border-color);
		  border-bottom: 1px dotted var(--border-color);
		  font-style: italic;
		  color: var(--alt1-color);
		  text-align: center;
	  }

	  ul {
		  margin: 0;
		  padding: 0;
	  }

	  ul > li > ul {
		  padding-inline-start: 1em;
	  }

	  li {
		  list-style-type: none;
	  }

	  img {
		  vertical-align: middle;
	  }
	`);

	/** Injection des boutons de sélection et de création dans le tooltip. */
	export function wedBarActions(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcPointer): TemplateResult | null {
		if (isEltBoxSelection(srcDrawer)) {
			const bar = srcDrawer.focusBar;
			if (bar) return xhtml`<c-bar-actions i.="${{
				actions: bar.actions.filter((a) => a.getId() === "focusItem" || a.getId() === "fastSelect" || a.getId() === "create"),
				actionContext: bar.actionContext
			} as OActionableListInit<IWedletActionCtx>}"/>`;
		}
		return null;
	}

	export function detailsOrTypesTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcPointer): TemplateResult {
		if (!fields || fields.srcUri == null) return typesAllowedTpl(itemType, fields, srcDrawer);
		return xhtml`${SRCDRAWER.wedBarActions(itemType, fields, srcDrawer)}${SRCDRAWER.detailsTpl(itemType, fields, srcDrawer)}`;
	}

	export function detailsPreviewOrTypesTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcPointer): TemplateResult {
		if (!fields || fields.srcUri == null) return typesAllowedTpl(itemType, fields, srcDrawer);
		return xhtml`${SRCDRAWER.wedBarActions(itemType, fields, srcDrawer)}${SRCDRAWER.detailsPreviewTpl(itemType, fields, srcDrawer)}`;
	}

	/** SrcDrawer / SrcPointer dédié à pointer un espace d'un wsp. */
	export function spaceTpl(this: void, itemType: ItemType, fields: JSrcFields | null, srcDrawer: SrcDrawer): TemplateResult {
		return xhtml`<span class="spaceCode"><img class="icon" src="${itemType.wspMeta.getSpaceIcon()}" draggable="false"/><span class="code">${fields ? fields.srcUri || "Racine de l'atelier" : ""}</span></span>`;
	}
}
